import React from "react";
import PropTypes from "prop-types";
import { Typography, Box, Link } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { useTheme } from "@mui/styles";

const styles = (theme) => ({
  wrapper: {
    position: "relative",
    overflow: "hidden",
    padding: theme.spacing(8),
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(4),
    },
  },

  container: {
    display: "flex",
    padding: theme.spacing(2, 0),

    "& > div": {
      width: "100%",
      display: "flex",
      alignItems: "start",

      [theme.breakpoints.down("md")]: {
        justifyContent: "center",
      },

      [theme.breakpoints.down("sm")]: {
        justifyContent: "start",
      },
    },

    [theme.breakpoints.down("md")]: {
      flexWrap: "wrap",
      gap: theme.spacing(4),
    },
  },

  innerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "50%",
    margin: "0 auto",

    "& .contacts": {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      "& div": {
        width: "fit-content",
      },
    },

    "& .socials": {
      display: "flex",
      gap: theme.spacing(2),
      alignItems: "center",

      "& path": {
        fill: theme.palette.common.contrast,
      },

      [theme.breakpoints.down("md")]: {
        margin: "auto",
      },

      [theme.breakpoints.down("sm")]: {
        margin: "unset",
      },
    },

    [theme.breakpoints.down("md")]: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
    },

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "start",
      justifyContent: "start",
      width: "100%",
    },
  },

  link: {
    textDecoration: "none",
    color: theme.palette.common.textAlt,
    fontWeight: 300,
  },

  copyrightContainer: {
    padding: theme.spacing(4),
    borderTop: "1px solid" + theme.palette.common.text,
    margin: theme.spacing(4, 0),
    color: theme.palette.common.textAlt,

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4, 0),
      margin: 0,
    },
  },

  bgArtifact: {
    position: "relative",
    width: "100%",
    height: "100%",

    "& > div": {
      position: "absolute",
      backgroundColor: "rgba(148, 97, 255, 0.75)",
      boxShadow: "0px 0px 112px 32px rgb(148, 97, 255)",
      filter: "blur(100px)",
      aspectRatio: "1/1",
      width: "100%",
      margin: "0 auto",
      borderRadius: "100%",
    },
  },
});

const infos = [
  {
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="15"
        height="14"
        viewBox="0 0 15 14"
        fill="none"
      >
        <path
          d="M13.3152 10.6923C13.3152 10.9023 13.2685 11.1182 13.1693 11.3282C13.0702 11.5382 12.9418 11.7365 12.7727 11.9232C12.4868 12.2382 12.1718 12.4657 11.816 12.6115C11.466 12.7573 11.0868 12.8332 10.6785 12.8332C10.0835 12.8332 9.44768 12.6932 8.77685 12.4073C8.10602 12.1215 7.43518 11.7365 6.77018 11.2523C6.09935 10.7623 5.46352 10.2198 4.85685 9.619C4.25602 9.01234 3.71352 8.3765 3.22935 7.7115C2.75102 7.0465 2.36602 6.3815 2.08602 5.72234C1.80602 5.05734 1.66602 4.4215 1.66602 3.81484C1.66602 3.41817 1.73602 3.039 1.87602 2.689C2.01602 2.33317 2.23768 2.0065 2.54685 1.71484C2.92018 1.34734 3.32852 1.1665 3.76018 1.1665C3.92352 1.1665 4.08685 1.2015 4.23268 1.2715C4.38435 1.3415 4.51852 1.4465 4.62352 1.59817L5.97685 3.50567C6.08185 3.6515 6.15768 3.78567 6.21018 3.914C6.26268 4.0365 6.29185 4.159 6.29185 4.26984C6.29185 4.40984 6.25102 4.54984 6.16935 4.684C6.09352 4.81817 5.98268 4.95817 5.84268 5.09817L5.39935 5.559C5.33518 5.62317 5.30602 5.699 5.30602 5.79234C5.30602 5.839 5.31185 5.87984 5.32352 5.9265C5.34102 5.97317 5.35852 6.00817 5.37018 6.04317C5.47518 6.23567 5.65602 6.4865 5.91268 6.78984C6.17518 7.09317 6.45518 7.40234 6.75852 7.7115C7.07352 8.02067 7.37685 8.3065 7.68602 8.569C7.98935 8.82567 8.24018 9.00067 8.43852 9.10567C8.46768 9.11734 8.50268 9.13484 8.54352 9.15234C8.59018 9.16984 8.63685 9.17567 8.68935 9.17567C8.78852 9.17567 8.86435 9.14067 8.92852 9.0765L9.37185 8.639C9.51768 8.49317 9.65768 8.38234 9.79185 8.31234C9.92602 8.23067 10.0602 8.18984 10.206 8.18984C10.3168 8.18984 10.4335 8.21317 10.5618 8.26567C10.6902 8.31817 10.8243 8.394 10.9702 8.49317L12.901 9.864C13.0527 9.969 13.1577 10.0915 13.2218 10.2373C13.2802 10.3832 13.3152 10.529 13.3152 10.6923Z"
          stroke="#475569"
          stroke-width="1.5"
          stroke-miterlimit="10"
        />
      </svg>
    ),
    description: "+44 75954 70805",
  },
  {
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="15"
        height="14"
        viewBox="0 0 15 14"
        fill="none"
      >
        <path
          d="M10.416 11.9582H4.58268C2.83268 11.9582 1.66602 11.0832 1.66602 9.0415V4.95817C1.66602 2.9165 2.83268 2.0415 4.58268 2.0415H10.416C12.166 2.0415 13.3327 2.9165 13.3327 4.95817V9.0415C13.3327 11.0832 12.166 11.9582 10.416 11.9582Z"
          stroke="#475569"
          stroke-width="1.5"
          stroke-miterlimit="10"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M10.4173 5.25L8.59148 6.70833C7.99065 7.18667 7.00482 7.18667 6.40398 6.70833L4.58398 5.25"
          stroke="#475569"
          stroke-width="1.5"
          stroke-miterlimit="10"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
    description: "support@auralyze.ai",
  },
];

const socialIcons = [
  {
    icon: (
      <svg
        role="img"
        width="24px"
        height="24px"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <title>Instagram</title>
        <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
      </svg>
    ),
    label: "Instagram",
    href: "https://www.instagram.com/auralyze.ai/",
  },
  // {
  //   icon: (
  //     <svg
  //       width="24"
  //       height="24"
  //       viewBox="0 0 33 33"
  //       fill="none"
  //       xmlns="http://www.w3.org/2000/svg"
  //     >
  //       <path
  //         d="M32.5 23.204C32.5 29.028 29.028 32.5 23.204 32.5H21.3C20.42 32.5 19.7 31.78 19.7 30.9V21.668C19.7 21.236 20.052 20.868 20.484 20.868L23.3 20.82C23.524 20.804 23.716 20.644 23.764 20.42L24.324 17.364C24.372 17.076 24.148 16.804 23.844 16.804L20.436 16.852C19.988 16.852 19.636 16.5 19.62 16.068L19.556 12.148C19.556 11.892 19.764 11.668 20.036 11.668L23.876 11.604C24.148 11.604 24.356 11.396 24.356 11.124L24.292 7.28399C24.292 7.01198 24.084 6.804 23.812 6.804L19.492 6.86802C16.836 6.91602 14.724 9.092 14.772 11.748L14.852 16.148C14.868 16.596 14.516 16.948 14.068 16.964L12.148 16.996C11.876 16.996 11.668 17.204 11.668 17.476L11.716 20.516C11.716 20.788 11.924 20.996 12.196 20.996L14.116 20.964C14.564 20.964 14.916 21.316 14.932 21.748L15.076 30.868C15.092 31.764 14.372 32.5 13.476 32.5H9.796C3.972 32.5 0.5 29.028 0.5 23.188V9.796C0.5 3.972 3.972 0.5 9.796 0.5H23.204C29.028 0.5 32.5 3.972 32.5 9.796V23.204Z"
  //         fill="#33383B"
  //       />
  //     </svg>
  //   ),
  //   label: "Facebook",
  //   href: "https://facebook.com",
  // },
  {
    icon: (
      <svg
        role="img"
        width="24px"
        height="24px"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <title>LinkedIn</title>
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/auralyze-ai",
  },
  {
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 33 33"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7.77991 3.16553H5.89987C4.65987 3.16553 4.03321 4.67219 4.91321 5.55219L12.5532 13.1922C14.7399 15.3789 18.2732 15.3789 20.4598 13.1922L28.0999 5.55219C28.9799 4.67219 28.3532 3.16553 27.1132 3.16553H25.2332C24.1265 3.16553 23.0599 3.60553 22.2732 4.39219L17.4999 9.16553C16.9532 9.71219 16.0732 9.71219 15.5266 9.16553L10.7532 4.39219C9.95322 3.60553 8.88658 3.16553 7.77991 3.16553Z"
          fill="#33383B"
        />
        <path
          d="M7.77991 29.7922H5.89987C4.65987 29.7922 4.03321 28.2855 4.91321 27.4055L12.5532 19.7655C14.7399 17.5788 18.2732 17.5788 20.4598 19.7655L28.0999 27.4055C28.9799 28.2855 28.3532 29.7922 27.1132 29.7922H25.2332C24.1265 29.7922 23.0599 29.3522 22.2732 28.5655L17.4999 23.7922C16.9532 23.2455 16.0732 23.2455 15.5266 23.7922L10.7532 28.5655C9.95322 29.3522 8.88658 29.7922 7.77991 29.7922Z"
          fill="#33383B"
        />
      </svg>
    ),
    label: "Twitter",
    href: "https://twitter.com/auralyze_ai",
  },

  {
    icon: (
      <svg
        role="img"
        width="24px"
        height="24px"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <title>Tiktok</title>
        <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
      </svg>
    ),
    label: "Tiktok",
    href: "https://www.tiktok.com/@auralyze.ai",
  },
];

const menuItems = [
  {
    link: "/",
    name: "Home",
  },
  {
    link: "/blog",
    name: "Blog",
  },
  {
    link: "/video",
    name: "Videos",
  },
  {
    link: "/faq",
    name: "FAQs",
  },
  {
    link: "/pricing",
    name: "Pricing",
  },
  {
    link: "/community",
    name: "Community",
  },
];

function Footer(props) {
  const { classes } = props;

  const theme = useTheme();

  return (
    <Box
      className={classes.wrapper}
      style={{ background: theme.palette.mode === "light" ? "#fff" : "#000" }}
    >
      <Box className={classes.container}>
        <div className="logo">
          <img
            src={
              theme.palette.mode === "light"
                ? "/images/Auralyze_ai_black_logo.png"
                : "/images/auralyze_logo_white.png"
            }
            alt=""
          />
        </div>

        <div className="link">
          <div className={classes.innerContainer}>
            <Typography sx={{ margin: "10px 0" }} fontWeight={500}>
              Links
            </Typography>
            {menuItems.map((item, index) => (
              <Link key={index} href={item.link} className={classes.link}>
                <Typography fontWeight={400}>{item.name}</Typography>
              </Link>
            ))}
          </div>
        </div>

        <div className="contact">
          <div className={classes.innerContainer}>
            <Typography sx={{ margin: "10px 0" }} fontWeight={500}>
              Contact
            </Typography>
            {infos.map((info, index) => (
              <div key={index} className="contacts">
                <div>{info.icon}</div>
                <div>
                  <Typography sx={{ whiteSpace: "nowrap" }} fontWeight={400}>
                    {info.description}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="social">
          <div className={classes.innerContainer}>
            <div className="socials">
              {socialIcons.map((icon, index) => (
                <Link href={icon.href} key={index}>
                  {icon.icon}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </Box>

      <Box className={classes.copyrightContainer}>
        <Typography textAlign={"center"} fontSize={12}>
          Copyright © 2024 Auralyze.ai. All rights reserved.
        </Typography>
      </Box>

      {theme.palette.mode === "dark" && (
        <div className={classes.bgArtifact}>
          <div></div>
        </div>
      )}
    </Box>
  );
}

Footer.propTypes = {
  theme: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles, { withTheme: true })(Footer);
